import { useEffect, useMemo, useRef, useState } from "react";
import { Ban, Check, Copy, Eye, Loader2, Play, QrCode, RefreshCcw, Search, X, Zap } from "lucide-react";
import { toast } from "sonner";
import api from "../api";
import { useLanguage } from "../i18n";

const PAGE_SIZE = 25;

const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700",
  exhausted: "bg-amber-50 text-amber-700",
  suspended: "bg-slate-100 text-slate-500",
  expired: "bg-red-50 text-red-600",
};

function simStatus(s) {
  if (s.suspended) return "suspended";
  if (s.exhaustion_alerted) return "exhausted";
  if (s.created_at && s.validity) {
    try {
      const exp = new Date(s.created_at).getTime() + Number(s.validity) * 86400000;
      if (exp < Date.now()) return "expired";
    } catch { /* noop */ }
  }
  return "active";
}

function AdminUsageCell({ iccid }) {
  const { t } = useLanguage();
  const [u, setU] = useState(null);
  useEffect(() => {
    let on = true;
    api.get(`/admin/sims/${iccid}/usage`).then(({ data }) => { if (on) setU(data.data); }).catch(() => { if (on) setU({}); });
    return () => { on = false; };
  }, [iccid]);
  if (u === null) return <span className="text-xs text-slate-300">…</span>;
  if (u.usage_error) return <span className="text-xs text-red-400" title={u.usage_error}>n/d</span>;
  if (!u.total) return <span className="text-xs text-slate-400">—</span>;
  const pct = u.is_unlimited ? 100 : Math.max(0, Math.min(100, Math.round((u.remaining / u.total) * 100)));
  return (
    <div className="min-w-[120px]">
      <div className="h-1.5 rounded-full bg-slate-100 overflow-hidden">
        <div className={`h-full rounded-full ${pct > 20 ? "bg-[#0D9488]" : "bg-red-400"}`} style={{ width: `${pct}%` }} />
      </div>
      <div className="mt-1 font-mono-t text-[10px] text-slate-400">
        {u.is_unlimited ? "∞" : `${pct}% · ${(u.remaining / 1024).toFixed(2)}/${(u.total / 1024).toFixed(1)} GB`}
        {" · "}{u.activated ? t("admin.activated") : t("admin.notActivated")}
      </div>
    </div>
  );
}

export default function AdminSims({ query, onQueryChange, onTopup }) {
  const { t } = useLanguage();
  const [sims, setSims] = useState(null);
  const [page, setPage] = useState(0);
  const [busyId, setBusyId] = useState(null);
  const [copied, setCopied] = useState(null);
  const [qrSim, setQrSim] = useState(null);  // {token, title, iccid} — modale QR cliente
  const [detail, setDetail] = useState(null);  // {sim, usage} — modale dettaglio completo
  const debounce = useRef(null);

  const openDetail = async (s) => {
    setDetail({ sim: s, usage: null });
    try {
      const { data } = await api.get(`/admin/sims/${s.iccid}/usage`);
      setDetail({ sim: s, usage: data.data || {} });
    } catch {
      setDetail({ sim: s, usage: { usage_error: "fetch failed" } });
    }
  };

  const showQr = async (s) => {
    try {
      const { data } = await api.get(`/admin/esims/${s.iccid}/qr`);
      // endpoint QR protetto da login: scarico l'immagine con Authorization e la mostro come blob
      const img = await api.get(`/esims/qr-image/${data.data.qr_token}`, { responseType: "blob" });
      setQrSim({ url: URL.createObjectURL(img.data), title: data.data.package_title, iccid: s.iccid });
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    }
  };

  const load = (q = "") => api.get(`/admin/esims${q ? `?search=${encodeURIComponent(q)}` : ""}`)
    .then(({ data }) => setSims(data.data || [])).catch(() => setSims([]));

  useEffect(() => { load(query); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const onSearch = (v) => {
    onQueryChange(v);
    setPage(0);
    clearTimeout(debounce.current);
    debounce.current = setTimeout(() => load(v), 350);
  };

  const items = sims || [];
  const pages = Math.max(1, Math.ceil(items.length / PAGE_SIZE));
  const pageItems = items.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);
  const stats = useMemo(() => ({
    total: items.length,
    active: items.filter((s) => simStatus(s) === "active").length,
    suspended: items.filter((s) => s.suspended).length,
    exhausted: items.filter((s) => s.exhaustion_alerted).length,
  }), [items]);

  const suspend = async (s) => {
    setBusyId(s.iccid);
    try {
      await api.post(`/admin/sims/${s.iccid}/suspend`, { suspended: !s.suspended });
      await load(query);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("misc.error"));
    } finally {
      setBusyId(null);
    }
  };

  const fmtDate = (iso) => (iso ? iso.slice(0, 10).split("-").reverse().join("/") : "—");
  const expDate = (s) => {
    try {
      if (!s.created_at || !s.validity) return "—";
      return new Date(new Date(s.created_at).getTime() + Number(s.validity) * 86400000).toISOString().slice(0, 10).split("-").reverse().join("/");
    } catch { return "—"; }
  };
  const th = "text-left font-mono-t text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 px-4 py-3";
  const td = "px-4 py-3.5 text-sm text-slate-700 align-middle";

  return (
    <div data-testid="admin-sims-console" className="p-5 sm:p-7 min-w-[760px]">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-6">
          {[["total", stats.total], ["active", stats.active], ["suspended", stats.suspended], ["exhausted", stats.exhausted]].map(([k, v]) => (
            <div key={k}>
              <div className="font-mono-t text-[9px] font-bold uppercase tracking-[0.2em] text-slate-400">{t(`console.${k}`)}</div>
              <div data-testid={`admin-sims-stat-${k}`} className="font-display font-extrabold text-2xl text-slate-900">{v}</div>
            </div>
          ))}
        </div>
        <div className="flex flex-1 max-w-md items-center rounded-full border border-slate-200 bg-white overflow-hidden focus-within:border-[#FF6B35] transition-colors">
          <Search className="w-4 h-4 ml-5 text-slate-400 shrink-0" />
          <input data-testid="admin-sims-search" value={query} onChange={(e) => onSearch(e.target.value)}
            placeholder={t("admin.searchSims")} className="flex-1 px-4 py-2.5 bg-transparent outline-none text-sm font-mono-t" />
        </div>
      </div>

      {sims === null ? (
        <div className="py-16 text-center"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#FF6B35]" /></div>
      ) : pageItems.length === 0 ? (
        <p data-testid="admin-sims-empty" className="py-12 text-left text-sm text-slate-400">{t("console.empty")}</p>
      ) : (
        <table data-testid="admin-esims-table" className="w-full min-w-[1020px]">
          <thead><tr className="border-b border-slate-100">
            <th className={th}>ICCID</th><th className={th}>{t("fail.colUser")}</th><th className={th}>{t("console.package")}</th>
            <th className={th}>{t("console.status")}</th><th className={th}>{t("console.data")}</th>
            <th className={th}>{t("console.activated")}</th><th className={th}>{t("console.expires")}</th><th className={th}>{t("console.actions")}</th>
          </tr></thead>
          <tbody>
            {pageItems.map((s) => (
              <tr key={s.iccid} data-testid={`admin-sim-row-${s.iccid}`} className="border-b border-slate-50 hover:bg-[#FAF5EE] transition-colors">
                <td className={td}>
                  <button data-testid={`admin-sim-copy-${s.iccid}`} title={s.iccid}
                    onClick={() => { navigator.clipboard.writeText(s.iccid); setCopied(s.iccid); setTimeout(() => setCopied(null), 1500); }}
                    className="inline-flex items-center gap-1.5 font-mono-t text-xs font-bold text-slate-800 hover:text-[#FF6B35] transition-colors">
                    …{s.iccid.slice(-10)}
                    {copied === s.iccid ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5 text-slate-300" />}
                    {s.mock && <span className="text-amber-500 font-bold">DEMO</span>}
                  </button>
                </td>
                <td className={`${td} text-xs`}>{s.user_email}</td>
                <td className={td}>
                  <div className="font-medium text-slate-800">{s.package_title}</div>
                  <div className="text-xs text-slate-400">{s.destination}</div>
                </td>
                <td className={td}>
                  <span data-testid={`admin-sim-status-${s.iccid}`} className={`rounded-full px-3 py-1 font-mono-t text-[9px] font-bold uppercase tracking-widest ${STATUS_STYLE[simStatus(s)]}`}>
                    {t(`console.${simStatus(s)}`)}
                  </span>
                </td>
                <td className={td}><AdminUsageCell iccid={s.iccid} /></td>
                <td className={`${td} text-xs font-mono-t`}>{fmtDate(s.created_at)}</td>
                <td className={`${td} text-xs font-mono-t`}>{expDate(s)}</td>
                <td className={td}>
                  <div className="flex items-center gap-1.5">
                    <button data-testid={`admin-sim-detail-${s.iccid}`} onClick={() => openDetail(s)}
                      className="rounded-full p-2 border border-slate-200 text-slate-400 hover:border-[#1E293B] hover:text-[#1E293B] transition-colors" title={t("admin.simDetail")}>
                      <Eye className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`admin-sim-qr-${s.iccid}`} onClick={() => showQr(s)}
                      className="rounded-full p-2 border border-slate-200 text-slate-400 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors" title="QR">
                      <QrCode className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`admin-esim-topup-${s.iccid}`} onClick={() => onTopup(s)}
                      className="rounded-full bg-[#FF6B35] text-white p-2 hover:bg-[#E8551F] transition-colors" title={t("admin.topup")}>
                      <Zap className="w-3.5 h-3.5" />
                    </button>
                    <button data-testid={`admin-sim-suspend-${s.iccid}`} disabled={busyId === s.iccid} onClick={() => suspend(s)}
                      className={`rounded-full p-2 border transition-colors ${s.suspended ? "border-emerald-300 text-emerald-600 hover:bg-emerald-50" : "border-slate-200 text-slate-400 hover:border-red-400 hover:text-red-500"}`}
                      title={s.suspended ? t("console.resume") : t("console.suspend")}>
                      {busyId === s.iccid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : s.suspended ? <Play className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {pages > 1 && (
        <div className="mt-5 flex items-center justify-center gap-3" data-testid="admin-sims-pagination">
          <button data-testid="admin-sims-prev" disabled={page === 0} onClick={() => setPage((p) => p - 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">←</button>
          <span className="font-mono-t text-xs text-slate-400">{t("console.page")} {page + 1} {t("console.of")} {pages}</span>
          <button data-testid="admin-sims-next" disabled={page >= pages - 1} onClick={() => setPage((p) => p + 1)}
            className="rounded-full border border-slate-200 px-5 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-500 hover:border-[#FF6B35] hover:text-[#FF6B35] disabled:opacity-40 transition-colors">→</button>
        </div>
      )}

      {detail && (
        <div data-testid="admin-sim-detail-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#1E293B]/55 backdrop-blur-[2px]" onClick={() => setDetail(null)} />
          <div className="relative rounded-3xl bg-white p-6 sm:p-7 shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <button data-testid="admin-sim-detail-close" onClick={() => setDetail(null)} aria-label="chiudi"
              className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="font-mono-t text-[10px] font-bold uppercase tracking-[0.2em] text-[#FF6B35]">{t("admin.simDetail")}</div>
            <h3 className="mt-1 font-display font-bold text-xl text-slate-900">{detail.sim.package_title || detail.sim.title || "eSIM"}</h3>
            <div className="mt-1 flex items-center gap-2 font-mono-t text-xs text-slate-500">
              <span className="truncate">{detail.sim.iccid}</span>
              <button onClick={() => { navigator.clipboard.writeText(detail.sim.iccid); setCopied(detail.sim.iccid); setTimeout(() => setCopied(null), 1200); }}
                className="text-slate-400 hover:text-[#FF6B35] transition-colors" title="copia">
                {copied === detail.sim.iccid ? <Check className="w-3.5 h-3.5 text-emerald-500" /> : <Copy className="w-3.5 h-3.5" />}
              </button>
            </div>
            <div className="mt-1 text-sm text-slate-600">{detail.sim.user_email || "—"}</div>

            {/* grafico consumi come nell'area clienti */}
            <div className="mt-5 rounded-2xl bg-slate-50 border border-slate-200/70 p-4">
              <div className="font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-2">{t("console.dataLeft")}</div>
              {!detail.usage ? (
                <div className="flex items-center gap-2 text-slate-400 text-sm"><Loader2 className="w-4 h-4 animate-spin" /> …</div>
              ) : detail.usage.total ? (
                <>
                  <div className="h-2.5 rounded-full bg-slate-200 overflow-hidden">
                    <div className="h-full rounded-full bg-[#FF6B35] transition-all"
                      style={{ width: `${Math.max(0, Math.min(100, Math.round((detail.usage.remaining / detail.usage.total) * 100)))}%` }} />
                  </div>
                  <div className="mt-1.5 font-mono-t text-[11px] text-slate-500">
                    {Math.max(0, Math.min(100, Math.round((detail.usage.remaining / detail.usage.total) * 100)))}% · {(detail.usage.remaining / 1024).toFixed(2)}/{(detail.usage.total / 1024).toFixed(1)} GB
                    {detail.usage.airalo_status ? ` · ${detail.usage.airalo_status}` : ""}
                  </div>
                </>
              ) : (
                <div className="text-sm text-slate-400">{detail.usage.usage_error || "—"}</div>
              )}
              {detail.usage?.owner_email && (
                <div className="mt-2 text-xs text-slate-500">{t("admin.ownerWallet")}: <strong>€ {detail.usage.owner_wallet}</strong></div>
              )}
            </div>

            <div className="mt-4 grid grid-cols-2 gap-2 text-xs text-slate-500">
              <div className="rounded-xl bg-slate-50 px-3 py-2">{t("console.status")}: <strong className="text-slate-800">{t(`console.${simStatus(detail.sim)}`)}</strong></div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">{t("admin.autoTopup")}: <strong className="text-slate-800">{detail.sim.auto_topup ? "ON" : "OFF"}</strong></div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">{t("admin.created")}: <strong className="text-slate-800">{detail.sim.created_at ? new Date(detail.sim.created_at).toLocaleDateString() : "—"}</strong></div>
              <div className="rounded-xl bg-slate-50 px-3 py-2">{t("console.expires")}: <strong className="text-slate-800">{detail.sim.expires_at ? new Date(detail.sim.expires_at).toLocaleDateString() : "—"}</strong></div>
            </div>

            <div className="mt-5 flex flex-wrap gap-2">
              <button data-testid="admin-detail-qr" onClick={() => { const s = detail.sim; setDetail(null); showQr(s); }}
                className="rounded-full border border-slate-200 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-[#FF6B35] hover:text-[#FF6B35] transition-colors inline-flex items-center gap-1.5">
                <QrCode className="w-3.5 h-3.5" /> QR
              </button>
              <button data-testid="admin-detail-topup" onClick={() => { const s = detail.sim; setDetail(null); onTopup(s); }}
                className="rounded-full bg-[#FF6B35] text-white px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest hover:bg-[#E8551F] transition-colors inline-flex items-center gap-1.5">
                <Zap className="w-3.5 h-3.5" /> {t("console.topup")}
              </button>
              <button data-testid="admin-detail-toggle"
                onClick={async () => { const s = detail.sim; setDetail(null); await suspend(s); }}
                className="rounded-full border border-slate-200 px-4 py-2 font-mono-t text-[10px] font-bold uppercase tracking-widest text-slate-600 hover:border-red-300 hover:text-red-500 transition-colors inline-flex items-center gap-1.5">
                {detail.sim.suspended ? <Play className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                {detail.sim.suspended ? t("console.resume") : t("console.suspend")}
              </button>
            </div>
          </div>
        </div>
      )}

      {qrSim && (
        <div data-testid="admin-sim-qr-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-[#1E293B]/55 backdrop-blur-[2px]" onClick={() => setQrSim(null)} />
          <div className="relative rounded-3xl bg-white p-6 shadow-2xl text-center max-w-xs w-full">
            <button data-testid="admin-sim-qr-close" onClick={() => setQrSim(null)} aria-label="chiudi"
              className="absolute top-3 right-3 text-slate-400 hover:text-slate-600 transition-colors">
              <X className="w-5 h-5" />
            </button>
            <div className="font-display font-bold text-slate-900">{qrSim.title}</div>
            <div className="font-mono-t text-[10px] text-slate-400 mt-1">…{qrSim.iccid.slice(-10)}</div>
            <img data-testid="admin-sim-qr-image" src={qrSim.url} alt="QR eSIM"
              className="mx-auto mt-4 w-52 h-52 rounded-2xl border border-slate-200" />
            <p className="mt-3 text-xs text-slate-500">{t("admin.qrHint")}</p>
          </div>
        </div>
      )}
    </div>
  );
}
